// ============================================================================
// The line under the first-night box in the change-the-cleaner window.
//
// A handover is one date (TCSD-0336). The window asks the incoming sub's first
// night and nothing else; this says, in plain words, what follows from it: the
// outgoing sub's last night is the night before, or — when the person picked the
// sub who was just replaced — that the change puts them back.
//
// Kept apart from the window on purpose: the window imports the database client
// at module scope, so it cannot be rendered in a test (TCSD-0049). This can.
// ============================================================================

import { fmtNight, nightBefore } from './nights';

export interface HandoverNoteProps {
  outgoingName: string | null;
  firstNight: string;
  /** Set when the chosen incoming sub is the one the outgoing sub replaced. */
  restate: null | {
    previousSubName: string | null;
    /** The outgoing sub's own first night here, if it is on file. */
    outgoingStart: string | null;
  };
}

/** Save stays off until the one date is chosen. */
export function cutoverReady(p: {
  busy: boolean;
  loading: boolean;
  incomingProviderId: string | null;
  firstNight: string;
}): boolean {
  return !p.busy && !p.loading && !!p.incomingProviderId && p.firstNight !== '';
}

export function HandoverNote({ outgoingName, firstNight, restate }: HandoverNoteProps) {
  const out = outgoingName ?? 'The previous sub';
  const last = firstNight ? fmtNight(nightBefore(firstNight)) : '';

  if (restate) {
    const back = restate.previousSubName ?? 'the previous sub';
    const cleanedNone =
      restate.outgoingStart != null && firstNight !== '' && restate.outgoingStart >= firstNight;
    return (
      <div
        className="rounded-md border border-dashed border-tcs-border px-3 py-2 text-[13px] text-tcs-text"
        data-testid="cutover-last-night"
      >
        <span className="mb-1 inline-block rounded-full border border-st-ok-border px-2 py-0.5 text-[11px] font-medium text-st-ok-fg-strong">
          Puts {back} back
        </span>
        <br />
        {restate.outgoingStart == null
          ? `${out}'s first night here is not on file, so this cannot tell whether they cleaned any nights. Saving will stop and ask for it to be put right directly.`
          : cleanedNone
            ? `${out} never cleaned a night here, so their line is removed and ${back}'s line carries on as if the change never happened.`
            : firstNight
              ? `${out} keeps the nights they cleaned, through ${last}. ${back} picks up again from ${fmtNight(firstNight)}.`
              : `Choose the night ${back} picks up again.`}
      </div>
    );
  }

  return (
    <div
      className="rounded-md border border-dashed border-tcs-border px-3 py-2 text-[13px] text-tcs-text"
      data-testid="cutover-last-night"
    >
      {firstNight ? (
        <>
          {out}&rsquo;s last night is <strong className="text-st-ok-fg-strong">{last}</strong>. Worked
          out from the date above — not asked.
        </>
      ) : (
        <>
          {out}&rsquo;s last night is worked out from the date above — the night before. Choose the
          first night to see it.
        </>
      )}
    </div>
  );
}
